import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import {
  CircularProgress,
  Divider,
  ListItemIcon,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuthStore } from '@/features/auth/stores/authStore';
import { useInformeFinal, useInformeParcial } from '@/features/informes/hooks/useInformes';
import { useNotify } from '@/hooks/useNotify';
import { abrirInformePDF } from '@/lib/abrirInformePDF';
import { Rol } from '@/types/enums';

import type { Protocolo } from '../types';
import { useActualizarCobrado } from '../hooks/useActualizarCobrado';
import { useActualizarFacturado } from '../hooks/useActualizarFacturado';
import { CerrarProtocoloDialog } from './CerrarProtocoloDialog';
import { EliminarProtocoloDialog } from './EliminarProtocoloDialog';

interface ProtocoloKebabMenuProps {
  protocolo: Protocolo;
  canManage: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
}

export function ProtocoloKebabMenu({
  protocolo,
  canManage,
  anchorEl,
  onClose,
}: ProtocoloKebabMenuProps): JSX.Element {
  const navigate = useNavigate();
  const notify = useNotify();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [cerrarOpen, setCerrarOpen] = useState(false);
  const currentUser = useAuthStore((s) => s.currentUser);
  const isAdmin = currentUser?.rol === Rol.ADMINISTRADOR;

  // enabled: false — solo se disparan con refetch() al hacer click
  const informeParcialQuery = useInformeParcial(protocolo.uid_protocolo);
  const informeFinalQuery = useInformeFinal(protocolo.uid_protocolo);

  const actualizarFacturado = useActualizarFacturado();
  const actualizarCobrado = useActualizarCobrado();

  const isClosed = protocolo.informe_final_emitido;
  const downloadQuery = isClosed ? informeFinalQuery : informeParcialQuery;

  function handleVerDetalle(): void {
    onClose();
    navigate(`/protocolos/${protocolo.uid_protocolo}`);
  }

  async function handleToggleFacturado(): Promise<void> {
    onClose();
    const nuevoValor = !protocolo.facturado;
    try {
      await actualizarFacturado.mutateAsync({
        uidProtocolo: protocolo.uid_protocolo,
        facturado: nuevoValor,
      });
      notify.success(
        nuevoValor ? 'Protocolo marcado como facturado.' : 'Protocolo marcado como no facturado.',
      );
    } catch {
      notify.error('Error al actualizar el estado. Intentá nuevamente.');
    }
  }

  async function handleToggleCobrado(): Promise<void> {
    onClose();
    const nuevoValor = !protocolo.cobrado;
    try {
      await actualizarCobrado.mutateAsync({
        uidProtocolo: protocolo.uid_protocolo,
        cobrado: nuevoValor,
      });
      notify.success(
        nuevoValor ? 'Protocolo marcado como cobrado.' : 'Protocolo marcado como no cobrado.',
      );
    } catch {
      notify.error('Error al actualizar el estado. Intentá nuevamente.');
    }
  }

  async function handleDescargar(): Promise<void> {
    const ok = await abrirInformePDF(
      async () => {
        const result = await downloadQuery.refetch();
        return result.data?.presigned_url;
      },
      notify.error,
      'No se pudo descargar el informe. Intentá de nuevo.',
    );
    if (ok) onClose();
  }

  function handleEliminar(): void {
    onClose();
    setDeleteOpen(true);
  }

  function handleCerrarProtocolo(): void {
    onClose();
    setCerrarOpen(true);
  }

  return (
    <>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={onClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={handleVerDetalle}>
          <ListItemIcon>
            <VisibilityOutlinedIcon fontSize="small" />
          </ListItemIcon>
          Ver detalle
        </MenuItem>

        {canManage && (
          <MenuItem onClick={() => void handleDescargar()} disabled={downloadQuery.isFetching}>
            <ListItemIcon>
              {downloadQuery.isFetching ? (
                <CircularProgress size={16} />
              ) : (
                <DownloadOutlinedIcon fontSize="small" />
              )}
            </ListItemIcon>
            {downloadQuery.isFetching ? 'Descargando...' : 'Descargar informe'}
          </MenuItem>
        )}

        {canManage && !isClosed && (
          <MenuItem onClick={handleCerrarProtocolo}>
            <ListItemIcon>
              <LockOutlinedIcon fontSize="small" />
            </ListItemIcon>
            Cerrar protocolo
          </MenuItem>
        )}

        {isAdmin && (
          <MenuItem onClick={() => void handleToggleFacturado()}>
            <ListItemIcon>
              <ReceiptLongOutlinedIcon fontSize="small" />
            </ListItemIcon>
            {protocolo.facturado ? 'Marcar como no facturado' : 'Marcar como facturado'}
          </MenuItem>
        )}

        {isAdmin && (
          <MenuItem onClick={() => void handleToggleCobrado()}>
            <ListItemIcon>
              <PaidOutlinedIcon fontSize="small" />
            </ListItemIcon>
            {protocolo.cobrado ? 'Marcar como no cobrado' : 'Marcar como cobrado'}
          </MenuItem>
        )}

        {canManage && (
          <>
            <Divider />
            <Tooltip title={isClosed ? 'No se puede eliminar un protocolo cerrado.' : ''}>
              <span>
                <MenuItem
                  onClick={handleEliminar}
                  disabled={isClosed}
                  sx={{ color: 'error.main' }}
                >
                  <ListItemIcon>
                    <DeleteOutlinedIcon
                      fontSize="small"
                      color={isClosed ? 'disabled' : 'error'}
                    />
                  </ListItemIcon>
                  Eliminar
                </MenuItem>
              </span>
            </Tooltip>
          </>
        )}
      </Menu>

      <EliminarProtocoloDialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        protocolo={protocolo}
      />

      <CerrarProtocoloDialog
        open={cerrarOpen}
        onClose={() => setCerrarOpen(false)}
        uidProtocolo={protocolo.uid_protocolo}
        numeroProtocolo={protocolo.numero_protocolo}
      />
    </>
  );
}
